import { Component, Input, OnInit } from '@angular/core';
import { FieldTypeEnum, InquiryModel, ExportFileTypeEnum } from 'app/models';
import { BoatInquiryService, ConfigService } from 'app/services';
import * as moment from 'moment';
import { LazyLoadEvent, MenuItem, MessageService } from 'primeng/api';
import { Utils } from 'app/utils';
import { XpressCalendarFilter } from 'app/components/xpress-calendar/xpress-calendar-filter';
import { TableLazyLoadEvent } from 'primeng/table';


@Component({
    selector: 'inquiry-detail',
    templateUrl: './inquiry-detail.component.html',
    styleUrls: ['./inquiry-detail.component.scss'],
    providers: [MessageService]
})
export class InquiryDetailComponent implements OnInit {
    @Input()
    public menuPermissionActive!: string;
    public breadcrumbs: MenuItem[] = [{ icon: 'icon_comms' }, { label: 'Comms' }, { label: 'Inquiries' }];
    public loading: boolean = true;
    public rowsPerPage: number = 15;
    public totalRecords: number = -1;
    public first: number = 0;
    public sortField: string | string[] = '';
    private lazyLoadHandled = true;
    public skip: number = 0;
    private sortOrder: number = 1;
    public inquiries!: any;
    private searchTimer: number = 0;
    public fieldTypeEnum = FieldTypeEnum;
    public exportLoading: boolean = false;
    public filterObj: XpressCalendarFilter = {
        startDate: moment(new Date()).format('YYYY-MM-DDTHH:mm:ss'), // Default Today Local Time
        endDate: moment(new Date()).format('YYYY-MM-DDTHH:mm:ss'), // Default Today Local Time
    };
    public isVisibleInqueryInfo: boolean = false;

    public selectedInquiry: any = undefined;

    public exportFileType = ExportFileTypeEnum;

    public cols: any[] = [
        {
            field: 'Id', header: 'Inquiry ID', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'Id', placeholder: 'Type here', active: false
        },
        {
            field: 'CustomerName', header: 'Customer Name', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'CustomerName', placeholder: 'Type here', active: false
        },
        {
            field: 'Email', header: 'Email', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'Email', placeholder: 'Type here', active: false
        },
        {
            field: 'Phone', header: 'Phone', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'Phone', placeholder: 'Type here', active: false
        },
        {
            field: 'BoatName', header: 'Boat Name/ID', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'BoatName', placeholder: 'Type here', active: false
        },
        {
            field: 'InquiryDate', header: 'Inquiry Date', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'InquiryDate', placeholder: 'Type here', active: false, disabled: true
        },
        {
            field: 'NumberOfPeople', header: 'Number of People', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'NumberOfPeople', placeholder: 'Type here', active: false
        },
        {
            field: 'RentalDuration', header: 'Rental Duration', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'RentalDuration', placeholder: 'Type here', active: false
        }
    ];

    public inquiryHeaderSearch: any = {
        Id: null,
        CustomerName: null,
        Email: null,
        Phone: null,
        BoatName: null,
        InquiryDate: null,
        NumberOfPeople: null,
        RentalDuration: null
    };

    constructor(
        private messageService: MessageService,
        public configService: ConfigService,
        public boatInquiryService: BoatInquiryService) { }

    ngOnInit(): void { }

    public handleLazyLoad(event: TableLazyLoadEvent): void {
        this.skip = event.first ? event.first : 0;

        if (event.rows) {
            this.rowsPerPage = event.rows;
        }

        if (!event.sortField) {
            this.sortField = 'Id';
            this.sortOrder = -1; // Desc
        }
        else {
            this.sortField = event.sortField;
            this.sortOrder = event.sortOrder ? event.sortOrder : 1;
        }

        this.getInquiries();
        this.lazyLoadHandled = false;
    }

    private getDto(): InquiryModel {
        const dto: InquiryModel = {
            Id: this.inquiryHeaderSearch.Id,
            CustomerName: this.inquiryHeaderSearch.CustomerName,
            Email: this.inquiryHeaderSearch.Email,
            Phone: this.inquiryHeaderSearch.Phone,
            BoatName: this.inquiryHeaderSearch.BoatName,
            InquiryDate: this.inquiryHeaderSearch.InquiryDate,
            NumberOfPeople: this.inquiryHeaderSearch.NumberOfPeople,
            RentalDuration: this.inquiryHeaderSearch.RentalDuration,
            Skip: this.skip,
            Take: this.rowsPerPage,
            SortOrder: this.sortOrder,
            SortField: this.sortField,
            FileType: null,
            TimeZoneOffset: Utils.getTimezoneOffset(),
            StartDate: moment(this.filterObj.startDate).format('YYYY-MM-DDTHH:mm:ss'),
            EndDate: moment(this.filterObj.endDate).format('YYYY-MM-DDTHH:mm:ss')
        };

        return dto;
    }

    private getInquiries() {
        this.loading = true;
        const dto = this.getDto();

        this.boatInquiryService.getAllInquiries(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    this.loading = false;
                    return;
                }

                this.inquiries = data.Items;
                this.inquiries.forEach((item: any) => {
                    item.InquiryDate = moment(item.InquiryDate).format('M/DD/YYYY');
                });

                this.totalRecords = data.TotalRecords;
                this.loading = false;
            })
            .catch(err => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
                this.loading = false;
            });
    }

    public handleHeaderFilterChange(event: any) {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            this.getInquiries();
            this.searchTimer = 0;
        }, 1500);
    }

    public exportReport(event: ExportFileTypeEnum) {
        const dto = this.getDto();
        dto.FileType = event;
        dto.Take = 0;
        dto.Skip = 0;
        this.exportLoading = true;

        this.boatInquiryService.exportBoatInquiries(dto, this.messageService)
            .then(data => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Success' });
                Utils.saveFileResponse(data, 'BoatInquiry' + moment(new Date()).format('DD MMM YYYY hh:mm:ss A'), dto.FileType);
                this.exportLoading = false;
            })
            .catch(err => {
                this.exportLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
            });
    }

    dateFilterChange(datesFilter: any) {
        this.filterObj.startDate = datesFilter.startDate;
        this.filterObj.endDate = datesFilter.endDate;
        this.handleHeaderFilterChange(0);
    }

    handlePopupLinkClick(inquiry: any): void {
        this.selectedInquiry = inquiry;
        this.isVisibleInqueryInfo = this.selectedInquiry !== undefined;
    }

    changedVisibleInquiryInfoEmit(value: boolean) {
        this.isVisibleInqueryInfo = false;
    }
}

